import { paths } from "@classdojo/ts-api-types";
import { useQuery, useMutation, UseMutationOptions } from "react-query";
import {
  makeMemberFetcher,
  makeCollectionFetcher,
  makeApiOperation,
  makeOperation,
} from "src/modules/reactQuery";
import { flagExpectedError } from "src/modules/errorHandling/axios";
import { useStoryBatchActionOperation } from "src/queries/storyBatchAction";
import { getApiClient } from "src/utils/axios";
import { isValidUploadPayload } from "src/utils/s3Upload";
import axios from "axios";
import { getUploadURL } from "./uploadFile";
import { components } from "@classdojo/ts-api-types";
import { AxiosError } from "axios";
import { NativeApp } from "src/utils/NativeApp";
import { resizeImage } from "src/utils/resizeImage";

export type SchoolStoryFeedResponse =
  paths["/api/dojoSchool/{schoolId}/storyFeed"]["get"]["responses"][200]["content"]["application/json"];

export type SchoolUpcomingEventResponse =
  paths["/api/dojoSchool/{targetId}/calendarEvent"]["get"]["responses"][200]["content"]["application/json"];

export type ClassUpcomingEventResponse =
  paths["/api/dojoClass/{targetId}/calendarEvent"]["get"]["responses"][200]["content"]["application/json"];

export type UpcomingEventItem =
  components["schemas"]["RenderedCalendarEventResponse"];

export type RenderedComment = components["schemas"]["RenderedComment"];

export type StoryFeedResponse =
  paths["/api/storyFeed"]["get"]["responses"][200]["content"]["application/json"];

export type StoryFeedItem = components["schemas"]["RenderedStoryPostResponse"];

export type StoryFeedItemContentAttachment =
  StoryFeedItem["contents"]["attachments"][0];

export type SchoolStoryFeedItemLikeResponse =
  paths["/api/dojoSchool/{schoolId}/storyFeed/{postId}/likes"]["get"]["responses"][200]["content"]["application/json"];

export type ClassStoryFeedItemLikeResponse =
  paths["/api/dojoClass/{classId}/storyFeed/{postId}/likes"]["get"]["responses"][200]["content"]["application/json"];

export const useGetUpcomingEventFetcher = makeMemberFetcher({
  pathPattern: "/api/dojoSchool/{targetId}/calendarEvent",
});

export const useGetCalendarEventFetcher = makeMemberFetcher({
  pathPattern: "/api/dojoSchool/{targetId}/calendarEvent/{eventId}",
});

export const useSchoolStoryFeedFetcher = makeCollectionFetcher({
  pathPattern: "/api/dojoSchool/{schoolId}/storyFeed",
  shouldInvalidate: (fetcherParams, mutation) => {
    if (
      useStoryBatchActionOperation.isSuccess(mutation) &&
      mutation.params.actions.some(
        (action) => action.action === "like" || action.action === "unlike"
      )
    ) {
      return true;
    }

    if (useStoryBatchActionOperation.isError(mutation)) return true;

    if (
      useCreateSchoolStoryPostCommentOperation.isSuccess(mutation) ||
      useCreateSchoolStoryPostCommentOperation.isError(mutation)
    ) {
      return true;
    }

    return false;
  },
});

export const useSchoolStoryPostFetcher = makeMemberFetcher({
  pathPattern: "/api/dojoSchool/{schoolId}/storyFeed/{postId}",
  shouldInvalidate: (fetcherParams, mutation) => {
    if (
      useStoryBatchActionOperation.isSuccess(mutation) ||
      useStoryBatchActionOperation.isError(mutation)
    ) {
      if (
        mutation.params.actions.some(
          (action: any) =>
            (action.action === "like" || action.action === "unlike") &&
            action.postId === fetcherParams.path.postId
        )
      ) {
        return true;
      }
    }

    if (useDeleteSchoolStoryPostFetcher.isSuccess(mutation)) {
      return true;
    }

    return false;
  },
  memberReducer: (fetcherParams, data, mutation) => {
    // optimistically update like status
    if (useStoryBatchActionOperation.isMutate(mutation)) {
      mutation.params.actions.forEach((action: any) => {
        if (
          (action.action === "like" || action.action === "unlike") &&
          action.postId === fetcherParams.path.postId
        ) {
          data = {
            ...data,
            likeCount:
              action.action === "like"
                ? data.likeCount + 1
                : data.likeCount - 1,
            likeButton: action.action === "like" ? "liked" : "unliked",
          };
        }
      });
    }

    return data;
  },
});

export const useSchoolStoryPostCommentsFetcher = makeCollectionFetcher({
  pathPattern: "/api/dojoSchool/{schoolId}/storyFeed/{postId}/comments",
  shouldInvalidate: (fetcherParams, mutation) => {
    if (
      useCreateSchoolStoryPostCommentOperation.isSuccess(mutation) &&
      mutation.params.schoolId === fetcherParams.path.schoolId &&
      mutation.params.postId === fetcherParams.path.postId
    ) {
      return true;
    }

    return false;
  },
});

interface CreateCommentOperationParams {
  classId?: string;
  schoolId?: string;
  postId: string;
  text: string;
}

export const useCreateSchoolStoryPostCommentOperation = makeOperation({
  mutationKey: "createSchoolStoryPostComment",
  mutationFn: ({ schoolId, postId, text }: CreateCommentOperationParams) =>
    flagExpectedError(
      [403],
      getApiClient().post(
        `/api/dojoSchool/${schoolId}/storyFeed/${postId}/comments?withStudentCommentsAndLikes=true`,
        { body: text }
      )
    ),
});

export const useDeleteSchoolStoryFeedComment = makeApiOperation({
  path: "/api/dojoSchool/{schoolId}/storyFeed/{postId}/comments/{commentId}",
  method: "delete",
});

export const useSchoolStoryPostViewsFetcher = makeMemberFetcher({
  pathPattern: "/api/dojoSchool/{targetId}/storyFeed/{postId}/views",
});

export const useSchoolStoryPostLikesFetcher = makeMemberFetcher({
  pathPattern: "/api/dojoSchool/{schoolId}/storyFeed/{postId}/likes",
});

type StoryPostAttachmentUploadPayload = {
  attachmentType: string;
  data?: any;
  mediaUrl?: string;
  contentType: string;
  preview?: string | null;
  filename?: string | null;
  size?: number;
  width?: number;
  height?: number;
};

async function uploadDataBlob(
  uploadUrl: string,
  data: any,
  contentType: string
) {
  let blob = data;
  if (contentType.startsWith("image/")) {
    blob = await resizeImage(uploadUrl, data, {
      maxSide: 7680, // max 8k resolution
      contentType,
    });
  }

  await axios.put(uploadUrl, blob, {
    headers: {
      "Content-Type": blob.type || contentType,
    },
  });
}

async function uploadMediaUrlViaFlutterBridge(
  mediaUrl: string,
  mediaType: string
) {
  const { success, ...otherProps } = await NativeApp.uploadMedia(
    mediaUrl,
    mediaType
  );

  if (!success) {
    throw new Error("Failed to upload local flutter media");
  }

  return otherProps;
}

export const uploadStoryPostAttachment = async (
  media: StoryPostAttachmentUploadPayload
) => {
  if (!isValidUploadPayload(media)) {
    throw new Error(
      `Invalid media upload payload: keys are ${Object.keys(media)}`
    );
  }

  const { data, mediaUrl, attachmentType, filename } = media;
  let { contentType, width, height } = media;

  let path;

  if (!!data) {
    const { uploadUrl, destinationUrl } = await getUploadURL(
      attachmentType,
      filename || ""
    );

    path = destinationUrl;

    if (!uploadUrl) {
      throw new Error("uploadUrl is undefined");
    }
    await uploadDataBlob(uploadUrl, data, contentType);
  } else if (!!mediaUrl) {
    const {
      downloadUrl,
      contentType: uploadedContentType,
      width: uploadWidth,
      height: uploadHeight,
    } = await uploadMediaUrlViaFlutterBridge(mediaUrl, contentType);
    path = downloadUrl;
    contentType = uploadedContentType;
    width = uploadWidth;
    height = uploadHeight;
  } else {
    throw new Error("Expected data or mediaUrl in attachment");
  }

  return {
    type: media.attachmentType,
    path,
    ...(width && height ? { dimensions: { width, height } } : {}),
    contentType,
  };
};

interface CreateStoryFeedParams {
  targetId: string;
  targetType?: string;
  caption: string;
  attachmentPayload?: Array<StoryPostAttachmentUploadPayload>;
  tags?: components["schemas"]["StoryFeedTag"][];
  privateStatus?: boolean;
}

export const useCreateSchoolStoryFeed = (
  options?: UseMutationOptions<void, Error, CreateStoryFeedParams>
) => {
  return useMutation<void, Error, CreateStoryFeedParams>(
    async ({ targetId, caption, attachmentPayload }) => {
      try {
        let uploadedAttachments: any;
        if (attachmentPayload) {
          uploadedAttachments = await Promise.all(
            attachmentPayload.map(
              async (attachment) => await uploadStoryPostAttachment(attachment)
            )
          );
        }
        const attachments = attachmentPayload?.map((attachment, index) => {
          return {
            contentType: attachment.contentType,
            ...uploadedAttachments?.[index],
          };
        });

        await getApiClient().post(`/api/dojoSchool/${targetId}/storyFeed`, {
          targetId,
          targetType: "school",
          body: caption,
          attachments: attachments || [],
        });
      } catch (err) {
        throw err;
      }
    },
    options
  );
};

export const useDeleteSchoolStoryPostFetcher = makeApiOperation({
  path: "/api/dojoSchool/{schoolId}/storyFeed/{postId}",
  method: "delete",
});

interface editStoryPostParams {
  schoolId: string;
  caption: string;
  postId: string;
  attachmentPayload?: StoryPostAttachmentUploadPayload;
  attachments?: components["schemas"]["Attachment"][];
}

export const useUpdateSchoolStoryPostFetcher = (
  options?: UseMutationOptions<void, void, editStoryPostParams>
) => {
  return useMutation<void, void, editStoryPostParams>(
    async ({ schoolId, caption, postId, attachmentPayload, attachments }) => {
      let uploadedAttachment;
      if (attachmentPayload) {
        uploadedAttachment = await uploadStoryPostAttachment(attachmentPayload);
      }
      if (!attachments && attachmentPayload) {
        attachments = [uploadedAttachment] as any;
      }
      if (attachments && uploadedAttachment && uploadedAttachment.path) {
        attachments[0].path = uploadedAttachment.path;
      }

      if (attachments) {
        await getApiClient().put(
          `/api/dojoSchool/${schoolId}/storyFeed/${postId}`,
          {
            body: caption,
            attachments,
          }
        );
      } else {
        await getApiClient().put(
          `/api/dojoSchool/${schoolId}/storyFeed/${postId}`,
          {
            body: caption,
            attachments: uploadedAttachment ? [uploadedAttachment] : [],
          }
        );
      }
    },
    options
  );
};

export const useCreateClassStoryFeed = (
  options?: UseMutationOptions<void, void, CreateStoryFeedParams>
) => {
  return useMutation<void, void, CreateStoryFeedParams>(
    async ({
      targetId,
      caption,
      attachmentPayload,
      tags,
      privateStatus = true,
    }) => {
      let uploadedAttachments: any;
      if (attachmentPayload) {
        uploadedAttachments = await Promise.all(
          attachmentPayload.map(
            async (attachment) => await uploadStoryPostAttachment(attachment)
          )
        );
      }
      const attachments = attachmentPayload?.map((attachment, index) => {
        return {
          contentType: attachment.contentType,
          ...uploadedAttachments?.[index],
        };
      });

      await getApiClient().post(`/api/dojoClass/${targetId}/storyFeed`, {
        targetId,
        targetType: "class",
        body: caption,
        attachments: attachments || [],
        tags,
        private: privateStatus,
      });
    },
    options
  );
};

export const useClassStoryFeedFetcher = makeCollectionFetcher({
  pathPattern: "/api/storyFeed",
  shouldInvalidate: (fetcherParams, mutation) => {
    if (
      useStoryBatchActionOperation.isSuccess(mutation) &&
      mutation.params.actions.some(
        (action) => action.action === "like" || action.action === "unlike"
      )
    ) {
      return true;
    }

    if (useStoryBatchActionOperation.isError(mutation)) return true;

    if (
      useCreateClassStoryPostCommentOperation.isSuccess(mutation) ||
      useCreateClassStoryPostCommentOperation.isError(mutation)
    ) {
      return true;
    }

    return false;
  },
  collectionReducer: (fetcherParams, data, mutation) => {
    console.log("COLLECTION REDUCER STORY FEED");
    // optimistically update like status
    if (useStoryBatchActionOperation.isMutate(mutation)) {
      mutation.params.actions.forEach((action) => {
        if (action.action === "like" || action.action === "unlike") {
          data = data.map((storyFeedItem) =>
            storyFeedItem._id !== action.postId
              ? storyFeedItem
              : {
                  ...storyFeedItem,
                  likeCount:
                    action.action === "like"
                      ? storyFeedItem.likeCount + 1
                      : storyFeedItem.likeCount - 1,
                  likeButton: action.action === "like" ? "liked" : "unliked",
                }
          );
        } else if (action.action === "read") {
          data = data.map((storyFeedItem) =>
            storyFeedItem._id !== action.postId || storyFeedItem.read
              ? storyFeedItem
              : {
                  ...storyFeedItem,
                  read: true,
                  readCount: storyFeedItem.readCount + 1,
                }
          );
        }
      });
      return data;
    }

    // optimistically update commentCount
    if (useCreateClassStoryPostCommentOperation.isSuccess(mutation)) {
      return data.map((storyFeedItem) =>
        storyFeedItem._id === mutation.params.postId &&
        storyFeedItem.targetId === mutation.params.classId
          ? {
              ...storyFeedItem,
              commentCount: storyFeedItem.commentCount + 1,
            }
          : storyFeedItem
      );
    }

    return data;
  },
});

export const useClassStoryPostFetcher = makeMemberFetcher({
  pathPattern: "/api/dojoClass/{classId}/storyFeed/{postId}",
  shouldInvalidate: (fetcherParams, mutation) => {
    if (
      (useStoryBatchActionOperation.isSuccess(mutation) ||
        useStoryBatchActionOperation.isError(mutation)) &&
      mutation.params.actions.some(
        (action) =>
          (action.action === "like" || action.action === "unlike") &&
          action.postId === fetcherParams.path.postId
      )
    ) {
      return true;
    }

    if (
      (useCreateClassStoryPostCommentOperation.isSuccess(mutation) ||
        (useCreateClassStoryPostCommentOperation.isError(mutation) &&
          mutation.type === "error" &&
          (mutation.error as AxiosError).response?.status === 403)) &&
      mutation.params.classId === fetcherParams.path.classId &&
      mutation.params.postId === fetcherParams.path.postId
    ) {
      return true;
    }

    return false;
  },
  memberReducer: (fetcherParams, data, mutation) => {
    // optimistically update like status
    if (useStoryBatchActionOperation.isMutate(mutation)) {
      mutation.params.actions.forEach((action) => {
        if (
          (action.action === "like" || action.action === "unlike") &&
          action.postId === fetcherParams.path.postId
        ) {
          data = {
            ...data,
            likeCount:
              action.action === "like"
                ? data.likeCount + 1
                : data.likeCount - 1,
            likeButton: action.action === "like" ? "liked" : "unliked",
          };
        } else if (
          action.action === "read" &&
          data._id === action.postId &&
          !data.read
        ) {
          data = {
            ...data,
            read: true,
            readCount: data.readCount + 1,
          };
        }
      });
      return data;
    }

    // optimistically update commentCount
    if (
      useCreateClassStoryPostCommentOperation.isSuccess(mutation) &&
      mutation.params.classId === fetcherParams.path.classId &&
      mutation.params.postId === fetcherParams.path.postId
    ) {
      return {
        ...data,
        commentCount: data.commentCount + 1,
      };
    }

    return data;
  },
});

export const useClassStoryPostCommentsFetcher = makeCollectionFetcher({
  pathPattern: "/api/dojoClass/{classId}/storyPost/{postId}/comments",
  shouldInvalidate: (fetcherParams, mutation) => {
    if (
      useCreateClassStoryPostCommentOperation.isSuccess(mutation) &&
      mutation.params.classId === fetcherParams.path.classId &&
      mutation.params.postId === fetcherParams.path.postId
    ) {
      return true;
    }

    return false;
  },
});

export const useCreateClassStoryPostCommentOperation = makeOperation({
  mutationKey: "createClassStoryPostComment",
  mutationFn: ({ classId, postId, text }: CreateCommentOperationParams) =>
    flagExpectedError(
      [403],
      getApiClient().post(
        `/api/dojoClass/${classId}/storyPost/${postId}/comments?withStudentCommentsAndLikes=true`,
        { body: text }
      )
    ),
});

export const useDeleteClassStoryFeedComment = makeApiOperation({
  path: "/api/dojoClass/{classId}/storyFeed/{postId}/comments/{commentId}",
  method: "delete",
});

export const useClassStoryPostLikesFetcher = makeCollectionFetcher({
  pathPattern: "/api/dojoClass/{classId}/storyFeed/{postId}/likes",
  shouldInvalidate: (fetcherParams, mutation) => {
    if (
      (useStoryBatchActionOperation.isSuccess(mutation) ||
        useStoryBatchActionOperation.isError(mutation)) &&
      mutation.params.actions.some(
        (action) =>
          (action.action === "like" || action.action === "unlike") &&
          action.postId === fetcherParams.path.postId
      )
    ) {
      return true;
    }

    return false;
  },
});

export const useClassStoryPostViewsFetcher = makeCollectionFetcher({
  pathPattern: "/api/dojoClass/{targetId}/storyFeed/{postId}/views",
  shouldInvalidate: (fetcherParams, mutation) => {
    if (
      (useStoryBatchActionOperation.isSuccess(mutation) ||
        useStoryBatchActionOperation.isError(mutation)) &&
      mutation.params.actions.some(
        (action) =>
          action.action === "read" &&
          action.postId === fetcherParams.path.postId
      )
    ) {
      return true;
    }

    return false;
  },
});

export type ClassStoryFeedViewsRequestFetcherResponse =
  paths["/api/dojoClass/{targetId}/storyFeed/{postId}/views"]["get"]["responses"][200]["content"]["application/json"];

export const useGetClassStoryFeedViewsRequestFetcher = (
  targetId: string,
  postId: string
) => {
  const path = `/api/dojoClass/${targetId}/storyFeed/${postId}/views?withStudentCommentsAndLikes=true`;
  return useQuery<ClassStoryFeedViewsRequestFetcherResponse, Error>(
    path,
    async () => {
      const response = await getApiClient().get<any>(path);
      return response.data;
    }
  );
};

export const useUpdateStoryCommentSettingOperation = makeApiOperation({
  path: "/api/dojoClass/{classId}/storyCommentSettings",
  method: "post",
});

export const useDisableSchoolStoryCommentsOperation = makeApiOperation({
  path: "/api/dojoSchool/{schoolId}/disableStoryComments",
  method: "post",
});

export const useUpdateClassStoryPostFetcher = makeApiOperation({
  path: "/api/dojoClass/{classId}/storyFeed/{postId}",
  method: "put",
});

export const useDeleteClassStoryPostFetcher = makeApiOperation({
  path: "/api/dojoClass/{classId}/storyFeed/{postId}",
  method: "delete",
});

export type PortfolioLikesRequestFetcherResponse =
  paths["/api/dojoClass/{classId}/storyPost/{postId}/likes"]["get"]["responses"][200]["content"]["application/json"];

export const useGetPortfolioLikesRequestFetcher = (
  classId: string,
  postId: string
) => {
  const path = `/api/dojoClass/${classId}/storyPost/${postId}/likes?withStudentCommentsAndLikes=true`;
  return useQuery<PortfolioLikesRequestFetcherResponse, Error>(
    path,
    async () => {
      const response = await getApiClient().get<any>(path);
      return response.data;
    }
  );
};

export const usePortfolioLikesFetcher = makeCollectionFetcher({
  pathPattern: "/api/dojoClass/{classId}/storyPost/{postId}/likes",
  shouldInvalidate: (fetcherParams, mutation) => {
    if (
      (useStoryBatchActionOperation.isSuccess(mutation) ||
        useStoryBatchActionOperation.isError(mutation)) &&
      mutation.params.actions.some(
        (action) =>
          (action.action === "like" || action.action === "unlike") &&
          action.postId === fetcherParams.path.postId
      )
    ) {
      return true;
    }

    return false;
  },
});

export type PortfolioViewsRequestFetcherResponse =
  paths["/api/dojoClass/{targetId}/storyPost/{postId}/views"]["get"]["responses"][200]["content"]["application/json"];

export const useGetPortfolioViewsRequestFetcher = (
  targetId: string,
  postId: string
) => {
  const path = `/api/dojoClass/${targetId}/storyPost/${postId}/views?withStudentCommentsAndLikes=true`;
  return useQuery<PortfolioViewsRequestFetcherResponse, Error>(
    path,
    async () => {
      const response = await getApiClient().get<any>(path);
      return response.data;
    }
  );
};

export const useGetClassUpcomingEventFetcher = makeMemberFetcher({
  pathPattern: "/api/dojoClass/{targetId}/calendarEvent",
});
